import { Th, Cell } from './Cells';
import { DrillSubhead, DrillSub, DrillSubfoot, DrillNote } from './DrillParts';
import { ShowAll } from './ShowAll';
import { useSort } from './useSort';
import { usePreview, DRILL_PREVIEW } from './usePreview';

const EMPTY = Object.freeze([]);

/**
 * The parts of one opened roll-up, as rows of the parent table.
 *
 * `expand` is the table's expand contract:
 *   `{ children(row), after?(row), columns?, key?(child, i), sort?, sortable?,
 *      preview?, noun?, note?(row, children), subhead?, rowClassName? }`
 * `children` returns the parts; `after` returns parts always drawn under the
 * preview, such as a pooled remainder; `columns` are the parts' columns — the
 * parent's by default, an array or a function of the parent row, as many as
 * the parent's; `note` closes the group with a sentence. Mounted per opened
 * row, so its sort and show-all start afresh each time.
 */
export function Parts({ parent, expand, parentColumns, span }) {
  const cols = typeof expand.columns === 'function'
    ? expand.columns(parent)
    : (expand.columns ?? parentColumns);
  const children = expand.children(parent) ?? EMPTY;
  const after = expand.after?.(parent) ?? EMPTY;
  const sorting = useSort(children, cols, expand.sort ?? null);
  const sortable = Boolean(expand.sortable) && cols.some((c) => c.sort);
  const { shown, all, total, toggle } = usePreview(sorting.sorted, expand.preview ?? DRILL_PREVIEW);
  const keyOf = expand.key ?? ((child, i) => child.key ?? child.symbol ?? child.id ?? i);
  const ctx = { parent };
  const note = expand.note?.(parent, children);
  const showFoot = note || all || shown.length < total;
  const rowClass = (child) => `dt-static ${expand.rowClassName?.(child) || ''}`.trim();

  return (
    <>
      {expand.subhead !== false && (
        <DrillSubhead>
          {cols.map((col) => <Th key={col.key} col={col} sort={sortable ? sorting : null} />)}
        </DrillSubhead>
      )}
      {shown.map((child, i) => (
        <DrillSub key={keyOf(child, i)} className={rowClass(child)}>
          {cols.map((col) => <Cell key={col.key} col={col} row={child} ctx={ctx} />)}
        </DrillSub>
      ))}
      {after.map((child, i) => (
        <DrillSub key={`after:${keyOf(child, i)}`} className={rowClass(child)}>
          {cols.map((col) => <Cell key={col.key} col={col} row={child} ctx={ctx} />)}
        </DrillSub>
      ))}
      {showFoot && (
        <DrillSubfoot colSpan={span}>
          <ShowAll total={total} shown={shown.length} all={all} onToggle={toggle} noun={expand.noun ?? 'rows'} />
          {note && <DrillNote>{note}</DrillNote>}
        </DrillSubfoot>
      )}
    </>
  );
}
